import axios from 'axios'
import { API_URL, API_TIMEOUT } from '../config'
import router from "@/router/index.js";

const api = axios.create({
    baseURL: API_URL,
    timeout: API_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
})

// Request interceptor for adding the auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor for handling common errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Handle 401 Unauthorized errors (token expired)
        if (error.response && error.response.status === 401) {
            if (router.currentRoute.value.path !== '/login') {
                localStorage.removeItem('token');
                router.push('/login?session_expired=true');
            }

        }

        return Promise.reject(error)
    }
)

// Add this function to set auth header explicitly
export const setAuthHeader = (token) => {
    if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete api.defaults.headers.common['Authorization'];
    }
};




export const authService = {
    login: async (email, password,recaptchaToken ) => {
        try {

            const response = await api.post('/login', { email, password, recaptchaToken  });
            return response.data;
        } catch (error) {
            if (error.response) {
                throw new Error(error.response.data.message || 'Login failed');
            }
            throw new Error('Network error occurred');
        }
    },

    register: async ({ fullName, email, phone, password, recaptchaToken }) => {
        try {
            const response = await api.post('/register', { fullName, email, phone, password, recaptchaToken });
            return response.data;
        } catch (error) {
            if (error.response) {
                throw new Error(error.response.data.message || 'Registration failed');
            }
            throw new Error('Network error occurred');
        }
    },

    logout: async () => {
        try {
            // If your API has a logout endpoint
             await api.post('/logout');

            // Clear token and user data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        } catch (error) {
            console.error('Logout error:', error);
        }
    }
};


export const fetchHomeData = async (timeout = 10000, retries = 2) => {
    console.log('Fetching home data from API...')

    for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeout)

        try {

            const response = await api.get('/home', {
                signal: controller.signal,
                timeout: timeout
            })

            clearTimeout(timeoutId)
            console.log('Home data fetched successfully')
            return response.data

        } catch (error) {
            clearTimeout(timeoutId)

            if (attempt === retries) {
                console.error(`Failed to fetch home data after ${retries + 1} attempts:`, error)

                // Return fallback data instead of throwing
                return {
                    categories: [],
                    scategories: [],
                    articles: [],
                    ads: [],
                    configurations: [],
                    shouldUseDAPricing: true,
                    country: 'INT',
                    user: null,
                    favoris: []
                }
            }

            // Exponential backoff before retry
            const delay = Math.pow(2, attempt) * 1000
            console.log(`Retry attempt ${attempt + 1} in ${delay}ms...`)
            await new Promise(resolve => setTimeout(resolve, delay))
        }
    }
}


export const fetchSubCategory = async (id) => {
    try {
        // Check if this path matches your Laravel API structure
        const response = await api.get(`/sous_categorie/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching subcategory ${id}:`, error);
        throw error;
    }
};

export const fetchArticles = async (subCategoryId) => {
    try {

        // Try first endpoint format
        try {
            const response = await api.get(`/sous_categorie/${subCategoryId}/articles`);
            return response.data;
        } catch (error1) {

            // Try second endpoint format
            try {
                const response = await api.get(`/articles/byscid/${subCategoryId}`);
                return response.data;
            } catch (error2) {

                // Try third endpoint format
                try {
                    const response = await api.get(`/articles/${subCategoryId}`);
                    return response.data;
                } catch (error3) {
                    throw new Error('All article endpoints failed');
                }
            }
        }
    } catch (error) {
        console.error(`Error fetching articles for subcategory ${subCategoryId}:`, error);
        throw error;
    }
};

export const updateUserPreferences = async (preferences) => {
    try {
        // Axios expects (url, data, config) not (url, {headers, body})
        const response = await api.post('/user/preferences', preferences, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error('API error:', error);
        if (error.response && error.response.data) {
            throw new Error(error.response.data.message || 'Failed to update preferences');
        }
        throw new Error('Failed to update preferences');
    }
};


export const placeDirectOrder = async (orderData) => {
    try {
        // Ensure required fields are present
        if (!orderData.article_id) {
            throw new Error('Article ID is required');
        }

        // Set quantity to 1 if not provided
        if (!orderData.quantity) {
            orderData.quantity = 1;
        }

        // Make API request to orders endpoint
        const response = await api.post('/orders', orderData);

        return response.data;
    } catch (error) {
        console.error('API error:', error);

        if (error.response && error.response.data) {
            throw new Error(error.response.data.message || 'Failed to place order');
        }

        throw new Error('Failed to place order. Please try again.');
    }
};

/**
 * Place a bulk order with multiple items
 * @param {Array} items - Array of order items
 * @param {String} paymentMethod - Selected payment method
 * @param {String} paymentNotes - Optional payment notes
 * @param {String} coupon_code - Optional payment notes
 * @param {File} paymentProof - Optional file for payment proof
 * @returns {Promise<Object>} Order confirmation details
 */
export const placeBulkOrder =
    async (items, paymentMethod, paymentNotes = null,
           paymentProof = null,coupon_code = null) => {
    try {
        // Prepare the FormData object for the multipart request
        const formData = new FormData();

        // Append each item as a separate key with indexed notation
        items.forEach((item, index) => {
            for (const key in item) {
                if (item[key] !== null) {
                    formData.append(`items[${index}][${key}]`, item[key]);
                }
            }
        });

        // Add payment method and notes as top-level fields
        formData.append('payment_method', paymentMethod);
        if (paymentNotes) {
            formData.append('payment_notes', paymentNotes);
        }

        // Add payment proof if provided
        if (paymentProof) {
            formData.append('payment_proof', paymentProof);
        }

        if (coupon_code) {
            formData.append('coupon_code', coupon_code);
        }

        // Make the API call with the correctly formatted FormData
        const response = await api.post('/bulk-orders', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        return response.data;
    } catch (error) {
        console.error('API error:', error);

        if (error.response && error.response.data) {
            throw new Error(error.response.data.message || 'Failed to place order');
        }

        throw new Error('Failed to place order. Please try again.');
    }
};


// Add this method in your coupon/promo section (or create one if it doesn't exist)
export const validateCoupon = async (code) => {
    try {
        const response = await api.post('/coupons/validate', {
            code: code
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};


export const updateProfile = async (profileData) => {
    try {
        const response = await api.put('/user/profile', profileData);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Update user password
 */
export const updatePassword = async (passwordData) => {
    try {
        const response = await api.put('/user/password', passwordData);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Get user orders
 */
export const getUserOrders = async (params = {}) => {
    try {
        const response = await api.get('/user/orders', { params });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Cancel user order
 */
export const cancelUserOrder = async (orderId) => {
    try {
        const response = await api.post(`/user/orders/${orderId}/cancel`);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Get user transactions
 */
export const getUserTransactions = async (params = {}) => {
    try {
        const response = await api.get('/user/transactions', { params });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Get user login history
 */
export const getUserLoginHistory = async (params = {}) => {
    try {
        const response = await api.get('/user/login-history', { params });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

export const getUserPaymentHistory = async (params) => {
    try{
    const response = await api.get('/user/payment-history', { params });
    return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};


/**
 * Report suspicious login
 */
export const reportSuspiciousLogin = async (loginId) => {
    try {
        const response = await api.post(`/user/login-history/${loginId}/report`);
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};

/**
 * Get payment methods for wallet charging
 */
export const getPaymentMethods = async () => {
    try {
        const response = await api.get('/payment-methods/charge');
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};


/**
 * Check FreeFire Player ID
 * @param {string} playerId - The player ID to validate
 * @returns {Promise} Response with player info or error
 */
export const checkFreeFirePlayer = async (playerId) => {
    try {
        const payload = {
            player_id: playerId
        };

        // This will use the authenticated API instance with token
        const response = await api.post('/freefire/check-player', payload);

        // Return the full response data
        return response.data;

    } catch (error) {
        console.error('FreeFire API error:', error);

        // If backend returns validation error, pass it through
        if (error.response && error.response.data) {
            return error.response.data;
        }

        // For network or other errors
        throw error;
    }
};
/**
 * Submit wallet charge request
 */
export const createChargeRequest  = async (formData) => {
    try {
        const response = await api.post('/user/charge-wallet', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    } catch (error) {
        console.error('API error:', error);
        throw error;
    }
};




export default api